import React, { useRef, useState, useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import "./VisitDataSummary.scss";
import CalendarViewWeekMode from "../../../components/CalendarViewWeekMode/CalendarViewWeekMode";
import { Select, Button } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { toPng } from "html-to-image";
import { jsPDF } from "jspdf";
import { httpService } from "../../../../services/httpService.service";
import { isAuthorized } from "../../../../services/authService";
import { RolesEnum } from "../../../../services/rolesEnum";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const { Option } = Select;

const TrainingAnalysis = () => {
  // Contract dates
  const contractStart = dayjs
    .utc(localStorage.getItem("Contract-startDate"))
    .local();
  const contractEnd = dayjs
    .utc(localStorage.getItem("Contract-endDate"))
    .local();

  // Default selected year/month = first contract date
  const [selectedYear, setSelectedYear] = useState(contractStart.year());
  const [selectedMonth, setSelectedMonth] = useState(
    contractStart.format("YYYY-MM")
  );
  const [selectedWeek, setSelectedWeek] = useState(1);

  const chartRef = useRef(null);

  // Generate available years
  const availableYearsOptions = useMemo(() => {
    const years = [];
    for (let y = contractStart.year(); y <= contractEnd.year(); y++)
      years.push(y);
    return years.map((year) => ({
      value: year,
      label: year === dayjs().year() ? `Current Year (${year})` : `${year}`,
    }));
  }, [contractStart, contractEnd]);

  // Generate available months for selected year
  const getAvailableMonthsOptions = (year) => {
    const startMonth =
      year === contractStart.year() ? contractStart.month() : 0; // 0-based
    const endMonth = year === contractEnd.year() ? contractEnd.month() : 11;
    return Array.from({ length: endMonth - startMonth + 1 }, (_, i) => {
      const monthIndex = startMonth + i;
      const monthValue = dayjs().year(year).month(monthIndex).format("YYYY-MM");
      const monthLabel = dayjs().year(year).month(monthIndex).format("MMMM");
      const currentMonth = dayjs().format("YYYY-MM");
      return {
        value: monthValue,
        label:
          monthValue === currentMonth
            ? `Current Month (${monthLabel})`
            : monthLabel,
      };
    });
  };

  // Get weeks in selected month, respecting contract range
  const getWeeksInMonth = (month) => {
    const [year, monthNum] = month.split("-").map(Number);
    const monthStart = dayjs(new Date(year, monthNum - 1, 1));
    const monthEnd = dayjs(
      new Date(year, monthNum - 1, monthStart.daysInMonth())
    );

    // Adjust start/end for contract
    const start = monthStart.isBefore(contractStart)
      ? contractStart
      : monthStart;
    const end = monthEnd.isAfter(contractEnd) ? contractEnd : monthEnd;

    const totalDays = end.diff(start, "day") + 1;
    return Math.ceil(totalDays / 7);
  };

  const totalWeeks = getWeeksInMonth(selectedMonth);

  const handleYearChange = (value) => {
    setSelectedYear(value);
    // Reset month to first available month in that year
    const firstMonth = getAvailableMonthsOptions(value)[0].value;
    setSelectedMonth(firstMonth);
    setSelectedWeek(1);
  };

  const handleMonthChange = (value) => {
    setSelectedMonth(value);
    setSelectedWeek(1); // reset week
  };

  const handleWeekChange = (value) => setSelectedWeek(value);

  const handleExportExcel = () => {
    const projectId = localStorage.getItem("currentProjectID");
    const params = {
      projectId,
      startDate: dayjs(selectedMonth).startOf("month"),
      endDate: dayjs(selectedMonth).endOf("month"),
      ipAnalysis: false,
      exportFilters: { includeTrainingAnalysis: true },
    };

    httpService.post(
      `/api/Site/exportVisitDataSummary`,
      params,
      (response) => {
        const blob = new Blob([response], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "Training Analysis.xlsx";
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      },
      (err) => console.error(err),
      { responseType: "arraybuffer" }
    );
  };

  const handleExportPDF = () => {
    if (!chartRef.current) return;
    toPng(chartRef.current, { cacheBust: true })
      .then((dataUrl) => {
        const pdf = new jsPDF();
        const imgWidth = 180;
        const imgHeight =
          (chartRef.current.offsetHeight / chartRef.current.offsetWidth) *
          imgWidth;
        pdf.addImage(dataUrl, "PNG", 15, 15, imgWidth, imgHeight);
        pdf.save("Training Analysis.pdf");
      })
      .catch((err) => console.error("Error exporting to PDF:", err));
  };

  const currentWeekInMonth = Math.min(selectedWeek, totalWeeks);

  return (
    <>
      <div className="row gap-4">
        <div className="col-12 col-lg-8 row gap-3">
          <div className="col-12 col-md-5 col-lg-3">
            <Select
              value={selectedYear}
              onChange={handleYearChange}
              options={availableYearsOptions}
            />
          </div>
          <div className="col-12 col-md-5 col-lg-3">
            <Select
              value={selectedMonth}
              onChange={handleMonthChange}
              options={getAvailableMonthsOptions(selectedYear)}
            />
          </div>
          <div className="col-12 col-md-5 col-lg-3">
            <Select value={selectedWeek} onChange={handleWeekChange}>
              {Array.from({ length: totalWeeks }, (_, i) => {
                const weekNumber = i + 1;
                return (
                  <Option
                    key={weekNumber}
                    value={weekNumber}
                  >{`Week ${weekNumber}`}</Option>
                );
              })}
            </Select>
          </div>
        </div>
        {isAuthorized([
          RolesEnum.SystemAdmin,
          RolesEnum.ProjectManager,
          RolesEnum.FieldCoordinator,
          RolesEnum.FieldTeamLeader,
        ]) && (
          <div className="row col-12 col-lg-4">
            <Button
              className="exportButtonTPM addButtonTPM col-12 col-lg-6"
              style={{
                padding: "5px 20px",
                fontSize: "13px",
                maxHeight: "35px",
              }}
              icon={<DownloadOutlined />}
              onClick={handleExportExcel}
            >
              Export to Excel
            </Button>
            <Button
              className="exportButtonTPM addButtonTPM col-12 col-lg-6"
              style={{
                padding: "5px 20px",
                fontSize: "13px",
                maxHeight: "35px",
                background: " var(--2, #6A113C) !important",
              }}
              icon={<DownloadOutlined />}
              onClick={handleExportPDF}
            >
              Export to PDF
            </Button>
          </div>
        )}
      </div>

      <div ref={chartRef} className="charts-container">
        <div className="calendarView">
          <div className="d-flex flex-column align-items-center w-100 gap-4">
            <h2>Count of Visits by State Level</h2>
          </div>
          <div className="calendarContainer w-100">
            <div className="tableHeader">
              <div className="month">
                {dayjs(selectedMonth).format("MMMM")} {selectedYear} - Week{" "}
                {selectedWeek}
              </div>
            </div>
            <CalendarViewWeekMode
              selectedWeek={selectedWeek}
              selectedMonth={selectedMonth}
              selectedYear={selectedYear}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default TrainingAnalysis;
